import { Core } from "@faire/design-tokens";
import { trackRetailersFilterInteraction } from "@faire/web-api/events/retailers/actionUnknown/filterInteraction";
import { trackRetailersFilterClickedClick } from "@faire/web-api/events/retailers/click/filterClicked";
import { IFilterSection } from "@faire/web-api/indigofair/data/IFilterSection";
import { Chevron } from "@faire/web/slate/icons/Chevron";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import ClickOutside from "@faire/web/ui/ClickOutside";
import React from "react";
import styled from "styled-components";

import { FilterSection } from "@faire/retailer/components/Filters/__internal__/FilterSection";
import { getSort } from "@faire/retailer/components/Filters/FilterUtils";
import { useFilterStore } from "@faire/retailer/components/Filters/useFilterStore";

interface IProps {
  name: string;
  className?: string;
}

export const Sort: React.FC<IProps> = ({ name, className }) => {
  const {
    data: { filterSections },
    setFilterState,
  } = useFilterStore(["filterSections"]);

  const [isFlyoutOpen, setIsFlyoutOpen] = React.useState(false);

  const sortSection = getSort(filterSections);

  if (!sortSection) {
    return null;
  }

  const handleFlyoutToggle = (event?: React.MouseEvent | Event) => {
    event?.stopPropagation();
    event?.preventDefault();
    trackRetailersFilterInteraction(
      name,
      !isFlyoutOpen ? "expand_section" : "collapse_section",
      sortSection.field_name ?? ""
    );

    setIsFlyoutOpen(!isFlyoutOpen);
  };

  const handleFilterSelected = (
    _section: IFilterSection,
    filterKey: string,
    _value: boolean
  ) => {
    trackRetailersFilterClickedClick(name, filterKey);
    setFilterState((currentState) => ({
      ...currentState,
      isNewFilterSelected: true,
    }));
    handleFlyoutToggle();
  };

  return (
    <Wrapper className={className}>
      <Header onClick={handleFlyoutToggle}>
        <Typography variant="paragraphSansRegular">
          {sortSection?.pill_text}
        </Typography>
        <StyledChevron
          direction={isFlyoutOpen ? "up" : "down"}
          fontSize="12px"
          color={Core.icon.default}
        />
      </Header>
      {isFlyoutOpen ? (
        <ClickOutside onClickOutside={handleFlyoutToggle}>
          <Flyout>
            <FilterSection
              section={sortSection}
              name={name}
              initiallyCollapsed={false}
              collapsable={false}
              onChange={handleFilterSelected}
              showAll
            />
          </Flyout>
        </ClickOutside>
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: max-content;
  height: 100%;
  cursor: pointer;
  ${responsiveMediaQueries.tabletAndBelow} {
    display: none;
  }
`;

const Flyout = styled(Column)`
  width: 200px;
  height: auto;
  max-height: 486px;
  background: ${Core.surface.inverse};
  box-shadow: 0 7px 10px rgba(0, 0, 0, 0.1);
  position: absolute;
  padding-left: ${getSpacing("3x")};
  padding-right: ${getSpacing("3x")};
  top: 42px;
  right: 0;
  cursor: default;
  z-index: 12;
`;

const Header = styled(Row).attrs({ justify: "space-between", align: "center" })`
  width: 100%;
  height: 100%;
  background: white;
  top: 0;
  z-index: 10;
  border: 1px solid ${Core.border.muted};
  padding: ${getSpacing(9, "2x")};
`;

const StyledChevron = styled(Chevron)`
  margin-left: ${getSpacing("1x")};
`;
